import React from 'react';
import { Route } from 'react-router-dom';
import styled, { css } from 'styled-components';

import AutolinkingDebugger from './AutolinkingDebugger';
import AutolinkingExampleImage from 'src/images/AutolinkingExampleImage.png';
import BasicLookupDebugger from './BasicLookupDebugger';
import BasicLookupExampleImage from 'src/images/BasicLookupExampleImage.png';
import CustomAttributesExampleImage from 'src/images/CustomAttributesExampleImage.png';
import CustomAttributesPage from './CustomAttributesPage';
import DebuggerFlowPage from './DebuggerFlowPage';
import { H1, H3 } from '@ad-scripts/presentation/components/common/headers';
import InsetContainer from '@ad-scripts/presentation/components/common/containers/inset_container';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import TableLayout from 'src/presentation/components/layouts/table/TableLayout';
import useRouter from 'src/presentation/hooks/useRouter';

type LookupDebuggingHomeProps = {
  root?: string;
};

const LookupDebuggingHome: React.FC<LookupDebuggingHomeProps> = ({ root }) => {
  const { match } = useRouter();

  return (
    <>
      <Route exact path={`${match.path}`} render={() => renderHomeContent(root)} />
      <Route exact path={`${root}/autolinking`} render={() => renderAutolinkingFlow(root)} />
      <Route exact path={`${root}/basic`} render={() => renderBasicFlow(root)} />
      <Route exact path={`${root}/custom-attributes`} render={() => renderCustomAttributes()} />
    </>
  );
};

const renderAutolinkingFlow = root => {
  return <DebuggerFlowPage root={root} content={<AutolinkingDebugger />} dataAid="autolinking" />;
};

const renderBasicFlow = root => {
  return <DebuggerFlowPage root={root} content={<BasicLookupDebugger />} dataAid="basic" />;
};

const renderCustomAttributes = () => {
  return <CustomAttributesPage />;
};

const renderHomeContent = root => {
  return (
    <TableLayout data-aid="debugging-home">
      <HeaderContainer>
        <H1 data-aid="header-title">Lookup Adapter Debugger</H1>
        <Description>
          Lookup Adapters allow Support Heroes on Gladly to link and populate a Gladly Customer Profile from an external
          system of record. The debugging tools test the specific workflows associated with your Lookup Adapters and
          mirror the experience Support Heroes have in Gladly. To learn more about all things Lookup, visit our{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://help.gladly.com/developer/docs/lookup-adapter-introduction#"
          >
            Lookup Help Docs.
          </a>
        </Description>
      </HeaderContainer>
      <Grid>
        <div>
          <ImageContainer>
            <StyledImage src={AutolinkingExampleImage}></StyledImage>
          </ImageContainer>
          <H3>Autolinking + Detailed Workflow</H3>
          <div>
            Test the Autolinking workflow to debug what happens when a Support Hero loads an unlinked Customer Profile
            on Gladly.
          </div>
          <StyledRouterButton to={`${root}/autolinking`}>Test Workflow</StyledRouterButton>
        </div>
        <div>
          <ImageContainer>
            <StyledImage src={BasicLookupExampleImage}></StyledImage>
          </ImageContainer>
          <H3>Basic + Detailed Workflow</H3>
          <div>
            Test the Basic Search workflow to debug what happens when a Support Hero manually links a Customer to an
            external system.
          </div>
          <StyledRouterButton to={`${root}/basic`}>Test Workflow</StyledRouterButton>
        </div>
        <div>
          <ImageContainer>
            <StyledImage src={CustomAttributesExampleImage}></StyledImage>
          </ImageContainer>
          <H3>Custom Attributes</H3>
          <div>See the Custom Attributes Gladly populates to the Customer Profile.</div>
          <StyledRouterButton to={`${root}/custom-attributes`} target="_blank" rel="noopener noreferrer">
            View Custom Attributes
          </StyledRouterButton>
        </div>
      </Grid>
    </TableLayout>
  );
};

const HeaderContainer = styled(InsetContainer).attrs({ inset: 'large' })`
  border-top-left-radius: ${p => p.theme.borderRadius.large};
  border-top-right-radius: ${p => p.theme.borderRadius.large};
  padding-bottom: 12px;
`;

const Description = styled.div`
  color: ${p => p.theme.colors.gray700};
  display: inline;
  line-height: 1.6;
`;

const Grid = styled.div`
  align-items: center;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  grid-gap: 1.5rem;
  margin: 24px;
  padding-bottom: 32px;
`;

const ImageContainer = styled(InsetContainer)`
  background-color: ${p => p.theme.colors.gray100};
  border-radius: ${p => p.theme.borderRadius.large};
  padding-bottom: 24px;
  margin-bottom: 24px;
`;

const buttonStyles = css`
  margin: 16px 0px;
  max-width: 180px;
`;

const StyledRouterButton = styled(RouterLinkButton)`
  ${buttonStyles}
  &[disabled] {
    color: ${p => p.theme.colors.gray700};
  }
`;

const StyledImage = styled.img`
  max-height: 180px;
  height: auto;
  object-fit: contain;
  width: auto;
`;

export default LookupDebuggingHome;
